"use client";

import React, { createContext, useContext, useEffect, useState } from "react";

// Types
export interface BrandColors {
  black: string;
  white: string;
  orange: string;
}

interface BrandThemeContextType {
  colors: BrandColors;
  isLoading: boolean;
}

// Default colors
const DEFAULT_COLORS: BrandColors = {
  black: "#000000",
  white: "#FFFFFF",
  orange: "#FF6A00",
};

// Context
const BrandThemeContext = createContext<BrandThemeContextType | null>(null);

// Helper: Convert hex to HSL string (for shadcn CSS variables)
function hexToHSL(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return "0 0% 0%";

  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// Apply colors to CSS custom properties
function applyCSSVariables(colors: BrandColors) {
  const root = document.documentElement;

  // Convert to HSL
  const blackHSL = hexToHSL(colors.black);
  const whiteHSL = hexToHSL(colors.white);
  const orangeHSL = hexToHSL(colors.orange);

  // Map simplified palette to shadcn semantic tokens
  // Background colors (white-based)
  root.style.setProperty("--background", whiteHSL);
  root.style.setProperty("--card", whiteHSL);
  root.style.setProperty("--popover", whiteHSL);

  // Text colors (black-based)
  root.style.setProperty("--foreground", blackHSL);
  root.style.setProperty("--card-foreground", blackHSL);
  root.style.setProperty("--popover-foreground", blackHSL);

  // Primary (orange-based)
  root.style.setProperty("--primary", orangeHSL);
  root.style.setProperty("--primary-foreground", whiteHSL);
  root.style.setProperty("--ring", orangeHSL);

  // Store raw hex values for components that need them
  root.style.setProperty("--brand-black", colors.black);
  root.style.setProperty("--brand-white", colors.white);
  root.style.setProperty("--brand-orange", colors.orange);
}

// Provider component
export function BrandThemeProvider({ children }: { children: React.ReactNode }) {
  const [colors] = useState<BrandColors>(DEFAULT_COLORS);
  const [isLoading, setIsLoading] = useState(true);

  // Apply CSS variables on mount
  useEffect(() => {
    applyCSSVariables(DEFAULT_COLORS);
    setIsLoading(false);
  }, []);

  return (
    <BrandThemeContext.Provider value={{ colors, isLoading }}>
      {children}
    </BrandThemeContext.Provider>
  );
}

// Hook to use brand theme
export function useBrandTheme() {
  const context = useContext(BrandThemeContext);
  if (!context) {
    throw new Error("useBrandTheme must be used within a BrandThemeProvider");
  }
  return context;
}
